import { generatePath } from '@toggle/helpers';
import {
  conversations,
  SearchConversationResponse,
  WebSearchSettings,
} from '@toggle/toggle';

import { useFlags } from '~/stores/use-flags/useFlags';
import { apiFetch, wretchRequest } from '~/utils/api-fetch/apiFetch';

import { Conversation, ConversationTask } from '../use-chat/use-chat.types';

export const FETCH_ALL_CONVERSATIONS_PAGE_SIZE = 50;

const isConversationV3Enabled = () =>
  useFlags.getState().getFeatureFlagState('scheduled_task_feature');

export type SubmittedAnalysisMode = 'Deep' | 'Auto';
export type WebSearch = 'restricted' | 'open' | 'block';

export interface GroupFilter {
  policy: 'Block';
  groups: string[];
}

type BasePostConversationArgs = {
  message: string;
  analysis_mode?: SubmittedAnalysisMode;
  tools_config?: {
    group_filter?: GroupFilter;
    parameters: {
      web_search?: {
        sources: string[];
      };
    };
  };
  attachments?: string[];
  controller?: AbortController;
};
type PostContinueConversationArgs = BasePostConversationArgs & {
  conversationId: string;
};

export const postConversation = async ({
  message,
  analysis_mode,
  tools_config,
  attachments,
  controller,
}: BasePostConversationArgs) => {
  const path = isConversationV3Enabled()
    ? 'conversation/v3'
    : conversations.postConversation.path;

  return apiFetch(path, {
    schema: conversations.postConversation.schema,
    method: 'post',
    body: {
      message,
      analysis_mode,
      tools_config,
      attachments,
    },
    signal: controller?.signal,
  });
};

export const postContinueConversation = async ({
  message,
  conversationId,
  analysis_mode,
  controller,
  tools_config,
  attachments,
}: PostContinueConversationArgs) => {
  const path = isConversationV3Enabled()
    ? `conversation/v3/${conversationId}`
    : conversations.continueConversation.path(conversationId);

  return apiFetch(path, {
    schema: conversations.continueConversation.schema,
    method: 'post',
    body: {
      message,
      analysis_mode,
      tools_config,
      attachments,
    },
    signal: controller?.signal,
  });
};

export const getConversationTasks = async (
  id: string,
  signal?: AbortSignal
) => {
  let page = 1;
  const allTasks: ConversationTask[] = [];
  let conversationData: Conversation | undefined = undefined;
  let hasMorePages = true;

  const isV3 = isConversationV3Enabled();

  while (hasMorePages) {
    const requestPath = generatePath({
      basePath: isV3
        ? `conversation/v3/${id}`
        : conversations.getConversationTask.path(id),
      params: {
        page,
      },
    });

    const result = await apiFetch(requestPath, {
      schema: conversations.getConversationTask.schema,
      signal,
    });

    if (result.error) {
      return result;
    }

    if (page === 1 && result.data) {
      conversationData = {
        ...result.data,
        public_copies: [],
      };
    }

    if (result.data?.tasks && result.data.tasks.length > 0) {
      allTasks.push(...result.data.tasks);
      page++;
    } else {
      hasMorePages = false;
    }
  }

  return {
    data: conversationData
      ? { ...conversationData, tasks: allTasks }
      : undefined,
  };
};

export const searchConversation = (query: string, page: number) => {
  const requestPath = generatePath({
    basePath: conversations.searchConversation.path,
    params: {
      q: query,
      page,
    },
  });

  return wretchRequest<SearchConversationResponse>(requestPath, {
    schema: conversations.searchConversation.schema,
  });
};

type GetConversationTaskStatesArgs = {
  conversationId: string;
  taskId: string;
};

export const getConversationTaskStates = async ({
  conversationId,
  taskId,
}: GetConversationTaskStatesArgs) => {
  const result = await apiFetch(
    conversations.getConversationMessage.path(conversationId, taskId),
    {
      schema: conversations.getConversationMessage.schema,
    }
  );

  return result;
};

export const updateConversation = ({
  id,
  name,
  favourite,
}: {
  id: string;
  name?: string;
  favourite?: boolean;
}) => {
  const body = favourite !== undefined ? { name, favourite } : { name };
  return apiFetch(conversations.conversationId.path(id), {
    method: 'put',
    body,
  });
};

export const deleteConversation = (id: string) =>
  apiFetch(conversations.conversationId.path(id), {
    method: 'delete',
  });

export const getAllConversations = (page: number) => {
  const requestPath = generatePath({
    basePath: isConversationV3Enabled()
      ? 'conversation/v3'
      : conversations.getAllConversations.path,
    params: {
      page,
      page_size: FETCH_ALL_CONVERSATIONS_PAGE_SIZE,
    },
  });

  return wretchRequest(requestPath, {
    schema: conversations.getAllConversations.schema,
  });
};

export const postFeedback = (body: {
  conversation_id: string;
  request_id: string;
  feedback: string;
}) => {
  wretchRequest(conversations.feedback.path, {
    method: 'post',
    body,
  });
};

export const abortRequest = (id: string) =>
  apiFetch(conversations.abortConversation.path(id), {
    method: 'put',
    body: { action: 'cancel' },
  });

export const copyConversation = (id: string, name: string) =>
  apiFetch(conversations.changeAccessLevel.path(id), {
    method: 'post',
    schema: conversations.changeAccessLevel.schema,
    body: {
      access_level: 'public',
      name,
    },
  });

export const postClonePublicConversation = (id: string) =>
  apiFetch(conversations.changeAccessLevel.path(id), {
    method: 'post',
    schema: conversations.changeAccessLevel.schema,
    body: {
      access_level: 'private',
    },
  });

export const getCustomSources = () =>
  apiFetch(conversations.getCustomSources.path, {
    schema: conversations.getCustomSources.schema,
  });

export const updateCustomSources = (sources: WebSearchSettings) =>
  apiFetch(conversations.updateCustomSources.path, {
    method: 'put',
    body: sources,
  });

export interface WebSearchToolsConfig {
  group_filter?: GroupFilter;
  parameters: { web_search: { sources: string[] } };
}

interface StoredToolsConfig {
  group_filter?: { policy: 'Allow' | 'Block'; groups: string[] };
  parameters?: {
    web_search?: string | { sources: string[] } | undefined;
  };
}

export const toolsConfigToWebSearch = (
  toolsConfig: StoredToolsConfig | undefined
): WebSearch => {
  if (toolsConfig?.group_filter?.policy === 'Block') {
    return 'block';
  }

  const ws = toolsConfig?.parameters?.web_search;

  if (
    typeof ws === 'string' &&
    (ws === 'open' || ws === 'restricted' || ws === 'block')
  ) {
    return ws;
  }

  if (
    ws &&
    typeof ws === 'object' &&
    'sources' in ws &&
    Array.isArray(ws.sources)
  ) {
    return ws.sources.length > 0 ? 'restricted' : 'open';
  }

  return 'open';
};

export const resolveWebSearchConfig = async (
  webSearch: WebSearch
): Promise<WebSearchToolsConfig> => {
  if (webSearch === 'restricted') {
    const result = await getCustomSources();
    return {
      parameters: { web_search: { sources: result.data?.sources ?? [] } },
      group_filter: undefined,
    };
  }
  if (webSearch === 'block') {
    return {
      parameters: { web_search: { sources: [] } },
      group_filter: { policy: 'Block', groups: ['web'] },
    };
  }
  return {
    parameters: { web_search: { sources: [] } },
    group_filter: undefined,
  };
};
